/**
 * Training samples management page.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'

import { apiClient, Sample, SampleCreate } from '@/services/api'
import { SampleList } from '@/components/Samples/SampleList'
import { SampleForm } from '@/components/Samples/SampleForm'
import { Loader } from '@/components/Common/Loader'

export const SamplesPage: React.FC = () => {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [editingSample, setEditingSample] = useState<Sample | null>(null)

  // Set token getter on mount
  useEffect(() => {
    if (getToken) {
      apiClient.setTokenGetter(getToken)
    }
  }, [getToken])

  // Fetch samples
  const {
    data: samplesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['samples'],
    queryFn: () => apiClient.listSamples(),
  })

  // Create sample mutation
  const createMutation = useMutation({
    mutationFn: (data: SampleCreate) => apiClient.createSample(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples'] })
      setShowForm(false)
      alert('Sample created successfully!')
    },
    onError: (error: Error) => {
      alert(`Failed to create sample: ${error.message}`)
    },
  })

  // Update sample mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SampleCreate }) =>
      apiClient.updateSample(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples'] })
      setShowForm(false)
      setEditingSample(null)
      alert('Sample updated successfully!')
    },
    onError: (error: Error) => {
      alert(`Failed to update sample: ${error.message}`)
    },
  })

  // Delete sample mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteSample(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples'] })
      alert('Sample deleted successfully!')
    },
    onError: (error: Error) => {
      alert(`Failed to delete sample: ${error.message}`)
    },
  })

  const handleAddNew = () => {
    setEditingSample(null)
    setShowForm(true)
  }

  const handleEdit = (sample: Sample) => {
    setEditingSample(sample)
    setShowForm(true)
  }

  const handleDelete = (sampleId: string) => {
    deleteMutation.mutate(sampleId)
  }

  const handleSubmit = (data: SampleCreate) => {
    if (editingSample) {
      updateMutation.mutate({ id: editingSample.sample_id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingSample(null)
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Loader message="Loading training samples..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="card bg-red-50 border border-red-200">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Samples</h3>
          <p className="text-red-600">{(error as Error).message}</p>
        </div>
      </div>
    )
  }

  const samples = samplesData?.samples || []

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Training Samples
            </h1>
            <p className="text-gray-600 max-w-2xl">
              Upload example note-report pairs to teach the AI your preferred style and structure.
              The more samples you provide, the better the AI understands your needs.
            </p>
          </div>
          <button onClick={handleAddNew} className="btn-primary">
            <svg
              className="inline-block w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add New Sample
          </button>
        </div>

        {/* Info Banner */}
        {samples.length > 0 && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-blue-600 mt-0.5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  {samples.length} Training Sample{samples.length !== 1 ? 's' : ''} Active
                </h4>
                <p className="text-sm text-blue-700">
                  When generating reports, the AI will use up to 3 of your most recent samples to
                  match your preferred format.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Samples List */}
      <SampleList
        samples={samples}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Sample Form Modal */}
      {showForm && (
        <SampleForm
          sample={editingSample}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  )
}
